/**
 * Deterministic per-item color palette for the segmentation-review
 * surface. Same id → same color, every time, forever — so a user
 * who refreshes mid-scan sees their "orange item" still orange,
 * and so a rail card's color matches its bbox + mask tint on the
 * stage.
 *
 * Ten distinct hues tuned to harmonize with Fidelity Purple
 * (#7d34b7) without competing with it. Avoid pure reds (reserved
 * for destructive CTAs) and pure greens (reserved for verified
 * badges). See `docs/SEGMENTATION_PIPELINE_PRD.md` §8 for the
 * thesis.
 *
 * Each palette entry exposes:
 *   - `solid`    — full-opacity fill (bbox stroke, rail card tab)
 *   - `tint`     — 0.55-alpha RGBA for mask overlay
 *   - `faint`    — 0.12-alpha RGBA for card background
 *   - `onSolid`  — accessible text color for labels on `solid`
 *
 * To add a new hue, extend `PALETTE` — the mod-based selector
 * picks up new entries automatically. Never delete a row (would
 * renumber every id's assignment).
 */

export interface PaletteEntry {
  name: string;
  solid: string;
  tint: string;
  faint: string;
  onSolid: string;
}

/**
 * Order matters for deterministic assignment. Appending is safe;
 * deleting or reordering changes every existing item's color.
 */
const PALETTE: readonly PaletteEntry[] = [
  {
    name: 'teal',
    solid: '#0D9488',
    tint: 'rgba(13, 148, 136, 0.55)',
    faint: 'rgba(13, 148, 136, 0.12)',
    onSolid: '#FFFFFF',
  },
  {
    name: 'amber',
    solid: '#D97706',
    tint: 'rgba(217, 119, 6, 0.55)',
    faint: 'rgba(217, 119, 6, 0.12)',
    onSolid: '#FFFFFF',
  },
  {
    name: 'rose',
    solid: '#E11D48',
    tint: 'rgba(225, 29, 72, 0.55)',
    faint: 'rgba(225, 29, 72, 0.12)',
    onSolid: '#FFFFFF',
  },
  {
    name: 'indigo',
    solid: '#4F46E5',
    tint: 'rgba(79, 70, 229, 0.55)',
    faint: 'rgba(79, 70, 229, 0.12)',
    onSolid: '#FFFFFF',
  },
  {
    name: 'emerald',
    solid: '#059669',
    tint: 'rgba(5, 150, 105, 0.55)',
    faint: 'rgba(5, 150, 105, 0.12)',
    onSolid: '#FFFFFF',
  },
  {
    name: 'fuchsia',
    solid: '#C026D3',
    tint: 'rgba(192, 38, 211, 0.55)',
    faint: 'rgba(192, 38, 211, 0.12)',
    onSolid: '#FFFFFF',
  },
  {
    name: 'coral',
    solid: '#F97316',
    tint: 'rgba(249, 115, 22, 0.55)',
    faint: 'rgba(249, 115, 22, 0.12)',
    onSolid: '#FFFFFF',
  },
  {
    name: 'sky',
    solid: '#0284C7',
    tint: 'rgba(2, 132, 199, 0.55)',
    faint: 'rgba(2, 132, 199, 0.12)',
    onSolid: '#FFFFFF',
  },
  {
    name: 'lime',
    solid: '#65A30D',
    tint: 'rgba(101, 163, 13, 0.55)',
    faint: 'rgba(101, 163, 13, 0.12)',
    onSolid: '#FFFFFF',
  },
  {
    name: 'violet',
    solid: '#8B5CF6',
    tint: 'rgba(139, 92, 246, 0.55)',
    faint: 'rgba(139, 92, 246, 0.12)',
    onSolid: '#FFFFFF',
  },
];

/**
 * Pick the palette entry for a given item id. Negative / missing
 * ids fall back to slot 0 (teal) — harmless and prevents the
 * rail from showing an empty colour for freshly-streamed items
 * whose id hasn't landed yet.
 */
export const colorFor = (id: number | null | undefined): PaletteEntry => {
  if (typeof id !== 'number' || !Number.isFinite(id)) return PALETTE[0];
  // JS % on negatives: (-3) % 10 === -3, so normalise first.
  const idx = ((Math.abs(Math.trunc(id)) % PALETTE.length) + PALETTE.length) % PALETTE.length;
  return PALETTE[idx];
};

/** Raw palette length (useful for tests + dev tooling). */
export const paletteSize = PALETTE.length;

/** Full palette export — readonly, for tests + storybook. */
export const palette: readonly PaletteEntry[] = PALETTE;
